import React, { useState, useEffect, useRef } from 'react';
import { dealsApi, documentsApi, usersApi, contactsApi } from '../services/api';
import { piApi } from '../services/piApi';
import { can } from '../lib/permissions';
import {
  Building2,
  Plus,
  Search,
  Calendar,
  DollarSign,
  CheckSquare,
  FileText,
  Users,
  File,
  Clock,
  ChevronDown,
  X,
  FolderOpen,
  ArrowLeft,
  AlertCircle,
  Trash2,
  Edit3,
  ChevronRight,
  UserCircle,
  StickyNote,
  Send,
  TrendingUp,
  MapPin,
  Award,
  Target,
  GitCommitHorizontal,
  Link,
  Shield,
  Upload,
  UserCheck,
  Layers,
} from 'lucide-react';
import './Deals.css';
import DiligenceTracker from './DiligenceTracker';
import { formatDocType } from '../utils/formatDocType';
import DocumentViewerModal from './DocumentViewerModal';
import DealTimeline from './DealTimeline';
import DealPermissions from './DealPermissions';
import IRLModule from './IRLModule';
import DealPortalTab from './DealPortalTab';
import './DealPortalTab.css';
import { DealGradeWidget, ThirtyDayCloseTracker } from './SaleLeaseback';



// ─── Comprehensive industry list ─────────────────────────
const INDUSTRIES = [
  // Agriculture & Natural Resources
  'Agriculture','Agritech','Farming','Aquaculture','Forestry','Fisheries',
  'Food Production','Crop Production','Livestock','Agricultural Equipment',
  'Agricultural Chemicals','Precision Agriculture','Vertical Farming',
  // Energy & Utilities
  'Oil & Gas','Renewable Energy','Solar Energy','Wind Energy','Hydropower',
  'Nuclear Energy','Energy Storage','Energy Infrastructure','Energy Trading',
  'Utilities','Smart Grid','Battery Technology','Hydrogen Energy','Biofuels',
  // Technology
  'Software','SaaS','Artificial Intelligence','Machine Learning',
  'Data Analytics','Big Data','Cloud Computing','Cybersecurity','DevOps',
  'IT Services','Hardware','Semiconductor','Robotics',
  'Internet of Things (IoT)','Quantum Computing','Blockchain','Web3',
  'Metaverse','Augmented Reality','Virtual Reality',
  // Financial Services
  'Banking','Fintech','Payments','Lending','Insurance','Insurtech',
  'Wealth Management','Asset Management','Hedge Funds','Venture Capital',
  'Private Equity','Cryptocurrency','Digital Assets','Capital Markets',
  'Mortgage Services','Credit Services',
  // Healthcare & Life Sciences
  'Healthcare','Healthtech','Biotechnology','Pharmaceuticals',
  'Medical Devices','Diagnostics','Telemedicine','Digital Health',
  'Healthcare IT','Genomics','Bioinformatics','Clinical Research',
  'Medical Research','Hospital Systems','Elder Care','Mental Health',
  // Consumer & Retail
  'Retail','E-commerce','Consumer Goods','Consumer Electronics',
  'Luxury Goods','Fashion & Apparel','Footwear','Beauty & Cosmetics',
  'Personal Care','Household Goods','Subscription Commerce',
  'Direct-to-Consumer (DTC)','Marketplaces',
  // Food & Beverage
  'Food & Beverage','Food Processing','Restaurants','Food Delivery',
  'Beverage Production','Alcoholic Beverages','Non-Alcoholic Beverages',
  'Specialty Foods','Packaged Foods','Nutrition','Plant-Based Foods',
  // Manufacturing & Industrial
  'Manufacturing','Industrial Equipment','Advanced Manufacturing',
  'Additive Manufacturing (3D Printing)','Industrial Automation',
  'Industrial Robotics','Chemicals','Specialty Chemicals','Plastics',
  'Metals & Mining','Materials Science',
  // Transportation & Mobility
  'Transportation','Logistics','Supply Chain','Freight','Shipping',
  'Trucking','Rail','Aviation','Aerospace','Automotive',
  'Electric Vehicles','Autonomous Vehicles','Fleet Management',
  'Last-Mile Delivery','Mobility Services',
  // Real Estate & Construction
  'Real Estate','Proptech','Commercial Real Estate',
  'Residential Real Estate','Real Estate Development',
  'Property Management','Construction','Construction Technology',
  'Infrastructure','Building Materials','Smart Buildings',
  // Media & Entertainment
  'Media','Entertainment','Film & Television','Streaming','Gaming',
  'Esports','Music','Publishing','Digital Media','Advertising',
  'Adtech','Content Creation',
  // Education
  'Education','EdTech','Online Learning','Higher Education',
  'K-12 Education','Corporate Training','Professional Certification',
  'Learning Platforms',
  // Hospitality & Travel
  'Hospitality','Hotels & Resorts','Travel','Tourism',
  'Vacation Rentals','Travel Technology','Cruise Lines','Event Management',
  // Telecommunications
  'Telecommunications','Wireless','Broadband','Satellite Communications',
  '5G Infrastructure','Network Infrastructure',
  // Government & Public Sector
  'Government Services','GovTech','Defense','Military Technology',
  'Public Infrastructure','Smart Cities',
  // Environmental & Sustainability
  'Climate Tech','Sustainability','Carbon Capture','Waste Management',
  'Recycling','Water Technology','Environmental Services','ESG Solutions',
  // Professional Services
  'Consulting','Legal Services','Accounting','Marketing Services',
  'Recruitment','HR Services','Outsourcing','Managed Services',
  // Sports & Recreation
  'Sports','Fitness','Sports Technology','Gyms & Fitness Centers',
  'Outdoor Recreation','Sporting Goods',
  // Emerging
  'Creator Economy','Influencer Marketing','Digital Identity',
  'Space Technology','Drone Technology','Pet Industry',
  'Dating Platforms','Social Networks','Market Research',
  'Community Platforms',
];

// ─── Industry typeahead input ────────────────────────────
function IndustryInput({ value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handle = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = query.length >= 1
    ? INDUSTRIES.filter(i => i.toLowerCase().includes(query.toLowerCase())).slice(0, 10)
    : [];

  const handleChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setOpen(true);
  };

  const handleSelect = (industry) => {
    setQuery(industry);
    onChange(industry);
    setOpen(false);
  };

  return (
    <div className="industry-input-wrapper" ref={containerRef}>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => filtered.length > 0 && setOpen(true)}
        placeholder={placeholder || 'Type to search industries…'}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="industry-dropdown">
          {filtered.map(ind => (
            <button
              key={ind}
              className="industry-option"
              onMouseDown={e => { e.preventDefault(); handleSelect(ind); }}
            >
              {ind}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const DEFAULT_STAGES = [
  { id: 'ioi',                   name: 'IOI',                      color: '#6B7280' },
  { id: 'loi',                   name: 'LOI',                      color: '#F59E0B' },
  { id: 'diligence_agreements',  name: 'Diligence & Agreement',    color: '#8B5CF6' },
  { id: 'closed',                name: 'Closing',                  color: '#10B981' },
  { id: 'post_close_integration',name: 'Post Close Completed',     color: '#0EA5E9' },
  { id: 'complete',              name: 'Complete',                 color: '#059669' },
  { id: 'terminated',            name: 'Terminated/Discontinued',  color: '#EF4444' },
];

function getStagesFromStorage() {
  try {
    const saved = localStorage.getItem('pipeline_stages');
    if (saved) {
      const parsed = JSON.parse(saved);
      // If saved stages still reference old L1/L2 ids, reset to defaults
      if (parsed.some(s => s.id === 'level_1_assessment' || s.id === 'level_2_assessment')) {
        localStorage.removeItem('pipeline_stages');
        return DEFAULT_STAGES;
      }
      return parsed;
    }
    return DEFAULT_STAGES;
  } catch {
    return DEFAULT_STAGES;
  }
}

const stages = getStagesFromStorage();

const DEAL_TYPES = [
  { value: '', label: 'Select type…' },
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'disposition', label: 'Disposition' },
  { value: 'investment', label: 'Investment' },
  { value: 'joint_venture', label: 'Joint Venture' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'sale_leaseback_industrial', label: 'Sale-Leaseback (Industrial)' },
  { value: 'other', label: 'Other' },
];

const DEAL_SOURCES = [
  { value: '', label: 'Select source…' },
  { value: 'inbound_auction', label: 'Inbound / Auction' },
  { value: 'inbound_1on1', label: 'Inbound / 1:1' },
  { value: 'internal_outreach', label: 'Internal Outreach' },
  { value: 'executive_relationship', label: 'Executive Relationship' },
  { value: 'sponsor_relationship', label: 'Sponsor Relationship' },
  { value: 'other', label: 'Other' },
];

const OUTCOME_REASONS = {
  closed: ['Full acquisition', 'Minority investment', 'Partnership agreement', 'Asset purchase'],
  passed: ['Valuation too high', 'Diligence findings', 'Market conditions', 'Strategic misfit', 'Capacity constraints', 'Management issues'],
  lost_competitor: ['Outbid by strategic', 'Outbid by financial sponsor', 'Seller preferred other terms', 'Process ended'],
  deal_died: ['Seller withdrew', 'Regulatory issues', 'Financing fell through', 'Material adverse change'],
};

const ARCHIVE_STAGES = ['archived', 'discontinued'];

// Normalise legacy stage IDs from old data so existing deals still resolve
function normaliseStage(stageId) {
  const legacyMap = {
    initial_review:       'ioi',
    screening:            'ioi',
    level_1_assessment:   'ioi',
    level_2_assessment:   'loi',
    due_diligence:        'diligence_agreements',
    final_agreements:     'diligence_agreements',
    negotiation:          'diligence_agreements',
    closing:              'closed',
    integration_completed:'post_close_integration',
    deal_closed:          'complete',
    archived:             'closed',
    discontinued:         'closed',
  };
  return legacyMap[stageId] || stageId || 'ioi';
}

function getStage(stageId) {
  return stages.find(s => s.id === normaliseStage(stageId)) || stages[0];
}

// ─────────────────────────────────────────────
// Main Deals component
// ─────────────────────────────────────────────

function Deals({ onOpenAnnotate, deals = [], setDeals, initialDeal, initialTab, onInitialDealConsumed, openCreate, onOpenCreateConsumed, currentUser, logAudit }) {
  const [documents, setDocuments] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  // Read customized pipeline stages from localStorage (set by Pipeline view)
  const [pipelineStages] = useState(() => getStagesFromStorage());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null); // deal object when editing, null when creating
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);

  // Create deal form
  const [newDealCodeName, setNewDealCodeName] = useState('');
  const [newDealName, setNewDealName] = useState('');
  const [newDealIndustry, setNewDealIndustry] = useState('');
  const [newDealValue, setNewDealValue] = useState('');
  const [newDealTargetClose, setNewDealTargetClose] = useState('');
  const [newDealStage, setNewDealStage] = useState('ioi');
  const [newDealOwner, setNewDealOwner] = useState('');
  const [newDealType, setNewDealType] = useState('');
  const [newDealSource, setNewDealSource] = useState('');
  const [newDealGeography, setNewDealGeography] = useState('');
  const [newDealLineOfBusiness, setNewDealLineOfBusiness] = useState('');
  const [newDealIsAddon, setNewDealIsAddon] = useState(false);
  const [newDealParentPortcoId, setNewDealParentPortcoId] = useState('');
  const [newDealLeaseTerm, setNewDealLeaseTerm] = useState('');
  const [newDealAnnualRent, setNewDealAnnualRent] = useState('');
  const [portcosList, setPortcosList] = useState([]);

  // Outcome exit modal
  const [outcomeModal, setOutcomeModal] = useState(null); // { deal, targetStage }

  useEffect(() => { fetchSupportingData(); }, []);

  // Open a deal passed in from Pipeline/task navigation
  const [pendingTab, setPendingTab] = useState(null);
  useEffect(() => {
    if (initialDeal) {
      setSelectedDeal(initialDeal);
      setPendingTab(initialTab || null);
      onInitialDealConsumed?.();
    }
  }, [initialDeal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open create modal when navigated from Pipeline "Create Deal" button
  useEffect(() => {
    if (openCreate) {
      setShowCreateModal(true);
      onOpenCreateConsumed?.();
    }
  }, [openCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSupportingData = async () => {
    try {
      const [docsRes, usersRes, portcosRes] = await Promise.all([
        documentsApi.list(),
        usersApi.list().catch(() => ({ data: [] })),
        piApi.getPortcos().catch(() => ({ data: [] })),
      ]);
      setDocuments(docsRes.data.documents || []);
      setOrgUsers(usersRes.data || []);
      setPortcosList(portcosRes.data || []);
    } catch (err) {
      console.error('Failed to fetch supporting data:', err);
    } finally {
      setLoading(false);
    }
  };

  const [createErrors, setCreateErrors] = useState({});

  const validateCreateDeal = () => {
    const errs = {};
    if (!newDealName.trim())    errs.name      = 'Deal name is required.';
    if (!newDealType)           errs.dealType  = 'Deal type is required.';
    if (!newDealSource)         errs.dealSource= 'Deal source is required.';
    if (!newDealIndustry.trim())errs.industry  = 'Industry is required.';
    return errs;
  };

  const handleCreateDeal = async () => {
    const errs = validateCreateDeal();
    if (Object.keys(errs).length) { setCreateErrors(errs); return; }
    setCreateErrors({});
    setIsCreating(true);
    const payload = {
      name: newDealName,
      codeName: newDealCodeName || null,
      industry: newDealIndustry,
      value: newDealValue ? parseFloat(newDealValue) : null,
      closingDate: newDealTargetClose,
      stage: newDealStage,
      owner: newDealOwner || null,
      dealType: newDealType || '',
      deal_type_id: newDealType === 'sale_leaseback_industrial' ? 'sale_leaseback_industrial' : null,
      vertical: newDealType === 'sale_leaseback_industrial' ? 'cre' : null,
      dealSource: newDealSource || '',
      geography: newDealGeography || '',
      lineOfBusiness: newDealLineOfBusiness || null,
      isAddon: newDealIsAddon,
      parentPortcoId: newDealIsAddon ? (newDealParentPortcoId || null) : null,
      proposedLeaseTerm: newDealType === 'sale_leaseback_industrial' ? (parseFloat(newDealLeaseTerm) || null) : null,
      proposedAnnualRent: newDealType === 'sale_leaseback_industrial' ? (parseFloat(newDealAnnualRent) || null) : null,
    };
    try {
      if (editingDeal) {
        const res = await dealsApi.update(editingDeal.id, payload);
        setDeals(prev => prev.map(d => d.id === editingDeal.id ? { ...d, ...res.data } : d));
        if (logAudit) logAudit('deal_updated', 'deals', `Updated deal: ${newDealName}`);
      } else {
        const res = await dealsApi.create(payload);
        setDeals(prev => [...prev, res.data]);
        if (logAudit) logAudit('deal_created', 'deals', `Created deal: ${newDealName}`);
      }
      setEditingDeal(null);
      resetForm();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Failed to save deal:', err);
      alert('Failed to save deal. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    try {
      const deletedDeal = deals.find(d => d.id === dealId);
      await dealsApi.delete(dealId);
      if (logAudit) logAudit('deal_deleted', 'deals', `Deleted deal: ${deletedDeal?.name || dealId}`);
      setDeals(prev => prev.filter(d => d.id !== dealId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete deal:', err);
    }
  };

  const handleMoveStage = async (deal, newStageId) => {
    try {
      const res = await dealsApi.update(deal.id, { stage: newStageId });
      if (logAudit) logAudit('deal_status_changed', 'deals', `"${deal.name}" moved to ${newStageId.replace(/_/g, ' ')}`);
      setDeals(prev => prev.map(d => d.id === deal.id ? res.data : d));
    } catch (err) {
      console.error('Failed to move deal:', err);
    }
  };

  const handleUpdateDeal = async (dealId, updates) => {
    try {
      const res = await dealsApi.update(dealId, updates);
      setDeals(prev => prev.map(d => d.id === dealId ? res.data : d));
      // Keep selectedDeal in sync
      setSelectedDeal(res.data);
    } catch (err) {
      console.error('Failed to update deal:', err);
    }
  };

  const resetForm = () => {
    setNewDealCodeName('');
    setNewDealName('');
    setNewDealIndustry('');
    setNewDealValue('');
    setNewDealTargetClose('');
    setNewDealStage('ioi');
    setNewDealOwner('');
    setNewDealType('');
    setNewDealSource('');
    setNewDealGeography('');
    setNewDealLineOfBusiness('');
    setNewDealIsAddon(false);
    setNewDealParentPortcoId('');
    setNewDealLeaseTerm('');
    setNewDealAnnualRent('');
  };

  const openEditDeal = (deal) => {
    setEditingDeal(deal);
    setNewDealCodeName(deal.codeName || '');
    setNewDealName(deal.name || '');
    setNewDealIndustry(deal.industry || '');
    setNewDealValue(deal.value || '');
    setNewDealTargetClose(deal.closingDate ? deal.closingDate.substring(0, 10) : '');
    setNewDealStage(normaliseStage(deal.stage) || 'ioi');
    setNewDealOwner(deal.owner || '');
    setNewDealType(deal.dealType || '');
    setNewDealSource(deal.dealSource || '');
    setNewDealGeography(deal.geography || '');
    setNewDealLineOfBusiness(deal.lineOfBusiness || '');
    setNewDealIsAddon(deal.isAddon || false);
    setNewDealParentPortcoId(deal.parentPortcoId || '');
    setNewDealLeaseTerm(deal.proposedLeaseTerm ?? '');
    setNewDealAnnualRent(deal.proposedAnnualRent ?? '');
    setCreateErrors({});
    setShowCreateModal(true);
  };

  // Intercept stage moves to archive/discontinued — open outcome modal
  const handleMoveStageWithOutcome = (deal, newStageId) => {
    if (ARCHIVE_STAGES.includes(newStageId)) {
      setOutcomeModal({ deal, targetStage: newStageId });
    } else {
      handleMoveStage(deal, newStageId);
    }
  };

  const handleOutcomeSubmit = async (deal, targetStage, outcomeData) => {
    try {
      const res = await dealsApi.update(deal.id, {
        stage: targetStage,
        outcome: outcomeData.outcome,
        outcomeReason: outcomeData.outcomeReason,
        outcomeDetails: outcomeData.outcomeDetails,
        finalValue: outcomeData.finalValue ? parseFloat(outcomeData.finalValue) : null,
        dealQualityScore: outcomeData.dealQualityScore || null,
        teamNotes: outcomeData.teamNotes,
        archivedAt: new Date().toISOString(),
      });
      if (logAudit) logAudit('deal_outcome_captured', 'deals', `"${deal.name}" → ${outcomeData.outcome}`);
      setDeals(prev => prev.map(d => d.id === deal.id ? res.data : d));
    } catch (err) {
      console.error('Failed to save outcome:', err);
    }
    setOutcomeModal(null);
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      notation: 'compact', maximumFractionDigits: 1,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const getStageBadge = (stageId) => {
    const stage = getStage(stageId);
    return (
      <span className="deal-stage-badge" style={{
        backgroundColor: `${stage.color}18`,
        color: stage.color,
        border: `1px solid ${stage.color}30`,
      }}>
        {stage.name}
      </span>
    );
  };

  // Active Diligence shows deals from Diligence & Agreement stage onwards
  const DILIGENCE_STAGES = ['diligence_agreements', 'closed', 'post_close_integration', 'complete', 'terminated'];
  const filteredDeals = deals.filter(deal => {
    const stage = normaliseStage(deal.stage);
    if (!DILIGENCE_STAGES.includes(stage)) return false;
    const matchesSearch =
      deal.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deal.industry?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === 'all' || stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  // ── Deal Detail view (render inline so modals defined below still mount)
  const selectedDealView = selectedDeal ? (() => {
    const latestDeal = deals.find(d => d.id === selectedDeal.id) || selectedDeal;
    return (
      <DealDetail
        deal={latestDeal}
        onBack={() => setSelectedDeal(null)}
        documents={documents}
        allDeals={deals}
        orgUsers={orgUsers}
        onOpenAnnotate={onOpenAnnotate}
        onUpdateDeal={handleUpdateDeal}
        currentUser={currentUser}
        logAudit={logAudit}
        onOpenEdit={openEditDeal}
        initialTab={pendingTab}
      />
    );
  })() : null;

  if (loading) {
    return (
      <div className="deals-container">
        <div className="deals-loading">
          <div className="deals-spinner" />
          <p>Loading deals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deals-container">
      {/* Deal Detail view — rendered here so modals below still mount */}
      {selectedDealView}

      {/* List view — hidden when a deal is selected */}
      {!selectedDeal && <>
      {/* Header */}
      <div className="deals-header">
        <div className="deals-title-section">
          <h1 className="deals-title">Deals</h1>
          <span className="deals-count">{deals.length} deal{deals.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="deals-header-actions">
          {can(currentUser, 'edit') && (
            <button className="deals-new-btn" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} /><span>New Deal</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="deals-filters">
        <div className="deals-search">
          <Search size={16} className="deals-search-icon" />
          <input type="text" placeholder="Search deals..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="deals-filter-dropdown">
          <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
            <option value="all">All stages</option>
            {DEFAULT_STAGES.filter(s => DILIGENCE_STAGES.includes(s.id)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <ChevronDown size={16} className="deals-filter-chevron" />
        </div>
      </div>

      {/* Deals list */}
      {deals.length === 0 ? (
        <div className="deals-empty">
          <div className="deals-empty-icon"><FolderOpen size={48} /></div>
          <h3>No deals yet</h3>
          <p>Create your first deal to get started</p>
          {can(currentUser, 'edit') && (
            <button className="deals-new-btn" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} /><span>Create Deal</span>
            </button>
          )}
        </div>
      ) : (
        <div className="deals-table-container">
          <table className="deals-table">
            <thead>
              <tr>
                <th className="deals-th-name">Deal Name</th>
                <th className="deals-th-industry">Industry</th>
                <th className="deals-th-stage">Stage</th>
                <th className="deals-th-value">Value</th>
                <th className="deals-th-close">Target Close</th>
                <th className="deals-th-updated">Updated</th>
                <th className="deals-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map(deal => (
                <tr key={deal.id} className="deals-row" onClick={() => setSelectedDeal(deal)}>
                  <td className="deals-td-name">
                    <div className="deals-name-cell">
                      <div className="deals-avatar">{deal.name?.charAt(0).toUpperCase()}</div>
                      <span className="deals-name-text">{deal.name}</span>
                    </div>
                  </td>
                  <td className="deals-td-industry">{deal.industry || '—'}</td>
                  <td className="deals-td-stage">{getStageBadge(deal.stage)}</td>
                  <td className="deals-td-value">{formatCurrency(deal.value)}</td>
                  <td className="deals-td-close">{formatDate(deal.closingDate)}</td>
                  <td className="deals-td-updated">{formatDate(deal.updatedAt || deal.createdAt)}</td>
                  <td className="deals-td-actions">
                    {can(currentUser, 'edit') && (
                      <button className="deals-delete-btn"
                        onClick={e => { e.stopPropagation(); setDeleteConfirm(deal); }}
                        title="Delete deal">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>}

      {/* Create / Edit Deal Modal */}
      {showCreateModal && (
        <div className="deals-modal-overlay" onClick={() => { setShowCreateModal(false); setEditingDeal(null); resetForm(); setCreateErrors({}); }}>
          <div className="deals-modal" onClick={e => e.stopPropagation()}>
            <div className="deals-modal-header">
              <h2>{editingDeal ? 'Edit Deal' : 'Create New Deal'}</h2>
              <button className="deals-modal-close" onClick={() => { setShowCreateModal(false); setEditingDeal(null); resetForm(); setCreateErrors({}); }}>
                <X size={20} />
              </button>
            </div>
            <div className="deals-modal-body">
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Company Name *</label>
                  <input
                    type="text" placeholder="Legal company name"
                    value={newDealName}
                    onChange={e => { setNewDealName(e.target.value); setCreateErrors(p => ({ ...p, name: '' })); }}
                    className={createErrors.name ? 'input-error' : ''}
                    autoFocus
                  />
                  {createErrors.name && <span className="deals-field-error">{createErrors.name}</span>}
                </div>
                <div className="deals-form-group">
                  <label>Code Name / Project Name</label>
                  <input
                    type="text" placeholder="e.g. Project Atlas"
                    value={newDealCodeName}
                    onChange={e => setNewDealCodeName(e.target.value)}
                  />
                </div>
              </div>
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Deal Type *</label>
                  <select
                    value={newDealType}
                    onChange={e => { setNewDealType(e.target.value); setCreateErrors(p => ({ ...p, dealType: '' })); }}
                    className={createErrors.dealType ? 'input-error' : ''}
                  >
                    {DEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {createErrors.dealType && <span className="deals-field-error">{createErrors.dealType}</span>}
                </div>
                <div className="deals-form-group">
                  <label>Deal Source *</label>
                  <select
                    value={newDealSource}
                    onChange={e => { setNewDealSource(e.target.value); setCreateErrors(p => ({ ...p, dealSource: '' })); }}
                    className={createErrors.dealSource ? 'input-error' : ''}
                  >
                    {DEAL_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  {createErrors.dealSource && <span className="deals-field-error">{createErrors.dealSource}</span>}
                </div>
              </div>
              {newDealType === 'sale_leaseback_industrial' && (
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Proposed Lease Term (years)</label>
                  <input type="number" placeholder="e.g. 12"
                    value={newDealLeaseTerm} onChange={e => setNewDealLeaseTerm(e.target.value)} />
                </div>
                <div className="deals-form-group">
                  <label>Proposed Annual Rent ($)</label>
                  <div className="deals-input-with-icon">
                    <DollarSign size={16} className="deals-input-icon" />
                    <input type="number" placeholder="e.g. 520000"
                      value={newDealAnnualRent} onChange={e => setNewDealAnnualRent(e.target.value)} />
                  </div>
                </div>
              </div>
              )}
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Industry *</label>
                  <IndustryInput
                    value={newDealIndustry}
                    onChange={v => { setNewDealIndustry(v); setCreateErrors(p => ({ ...p, industry: '' })); }}
                    placeholder="Type to search industries…"
                  />
                  {createErrors.industry && <span className="deals-field-error">{createErrors.industry}</span>}
                </div>
                <div className="deals-form-group">
                  <label>Geography</label>
                  <input type="text" placeholder="Country / Region"
                    value={newDealGeography} onChange={e => setNewDealGeography(e.target.value)} />
                </div>
              </div>
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Line of Business / Division</label>
                  <input type="text" placeholder="e.g. Technology, Healthcare, Consumer"
                    value={newDealLineOfBusiness} onChange={e => setNewDealLineOfBusiness(e.target.value)} />
                </div>
                <div className="deals-form-group">
                  <label>Deal Value ($M)</label>
                  <div className="deals-input-with-icon">
                    <DollarSign size={16} className="deals-input-icon" />
                    <input type="number" placeholder="e.g. 50 = $50M"
                      value={newDealValue} onChange={e => setNewDealValue(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Stage</label>
                  <select value={newDealStage} onChange={e => setNewDealStage(e.target.value)}>
                    {DEFAULT_STAGES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="deals-form-group">
                  <label>Owner</label>
                  <select value={newDealOwner} onChange={e => setNewDealOwner(e.target.value)}>
                    <option value="">Unassigned</option>
                    {orgUsers.map(u => (
                      <option key={u.id} value={u.full_name || u.name || u.email}>{u.full_name || u.name || u.email}</option>
                    ))}
                  </select>
                </div>
              </div>
              {['diligence_agreements', 'closed', 'post_close_integration', 'complete'].includes(newDealStage) && (
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Target Close</label>
                  <input type="date" value={newDealTargetClose}
                    onChange={e => setNewDealTargetClose(e.target.value)} />
                </div>
              </div>
              )}
              <div className="deals-form-row" style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginTop: 8 }}>
                <div className="deals-form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={newDealIsAddon} onChange={e => setNewDealIsAddon(e.target.checked)}
                      style={{ accentColor: '#4f46e5', width: 16, height: 16 }} />
                    This is an add-on acquisition
                  </label>
                </div>
                {newDealIsAddon && (
                  <div className="deals-form-group">
                    <label>Platform Company</label>
                    <select value={newDealParentPortcoId} onChange={e => setNewDealParentPortcoId(e.target.value)}>
                      <option value="">Select platform portco...</option>
                      {portcosList.filter(p => p.status === 'active').map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="deals-modal-footer">
              <button className="deals-btn-secondary" onClick={() => { setShowCreateModal(false); setEditingDeal(null); resetForm(); setCreateErrors({}); }}>
                Cancel
              </button>
              <button className="deals-btn-primary" onClick={handleCreateDeal} disabled={isCreating}>
                {isCreating ? 'Saving...' : editingDeal ? 'Save Changes' : 'Create Deal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Outcome Exit Modal */}
      {outcomeModal && (
        <OutcomeExitModal
          deal={outcomeModal.deal}
          targetStage={outcomeModal.targetStage}
          onSubmit={handleOutcomeSubmit}
          onCancel={() => setOutcomeModal(null)}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="deals-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="deals-modal deals-modal-small" onClick={e => e.stopPropagation()}>
            <div className="deals-modal-header">
              <h2>Delete Deal</h2>
              <button className="deals-modal-close" onClick={() => setDeleteConfirm(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="deals-modal-body">
              <p className="deals-confirm-text">
                Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>?
              </p>
              <p className="deals-confirm-subtext">
                This action cannot be undone.
              </p>
            </div>
            <div className="deals-modal-footer">
              <button className="deals-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="deals-btn-danger" onClick={() => handleDeleteDeal(deleteConfirm.id)}>
                Delete Deal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Kanban Board
// ─────────────────────────────────────────────
function KanbanBoard({ deals, stages, onSelectDeal, onDeleteDeal, onMoveStage, formatCurrency, formatDate }) {
  return (
    <div className="kanban-board">
      {stages.map(stage => {
        const stageDeals = deals.filter(d => normaliseStage(d.stage) === stage.id);
        return (
          <div key={stage.id} className="kanban-column">
            <div className="kanban-column-header" style={{ borderTop: `3px solid ${stage.color}` }}>
              <span className="kanban-column-title">{stage.name}</span>
              <span className="kanban-column-count"
                style={{ backgroundColor: `${stage.color}18`, color: stage.color }}>
                {stageDeals.length}
              </span>
            </div>
            <div className="kanban-column-body">
              {stageDeals.length === 0
                ? <div className="kanban-empty-col">No deals</div>
                : stageDeals.map(deal => (
                  <KanbanCard
                    key={deal.id}
                    deal={deal}
                    stage={stage}
                    stages={stages}
                    onSelect={() => onSelectDeal(deal)}
                    onDelete={() => onDeleteDeal(deal)}
                    onMoveStage={onMoveStage}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                  />
                ))
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({ deal, stage, stages, onSelect, onDelete, onMoveStage, formatCurrency, formatDate }) {
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  return (
    <div className="kanban-card" onClick={onSelect}>
      <div className="kanban-card-header">
        <div className="kanban-card-avatar"
          style={{ backgroundColor: `${stage.color}18`, color: stage.color }}>
          {deal.name?.charAt(0).toUpperCase()}
        </div>
        <div className="kanban-card-actions">
          {/* Move stage button */}
          <div className="kanban-move-wrapper">
            <button className="kanban-card-move"
              onClick={e => { e.stopPropagation(); setShowMoveMenu(p => !p); }}
              title="Move to stage">
              <ChevronRight size={13} />
            </button>
            {showMoveMenu && (
              <div className="kanban-move-menu" onClick={e => e.stopPropagation()}>
                <div className="kanban-move-menu-title">Move to</div>
                {stages.filter(s => s.id !== normaliseStage(deal.stage)).map(s => (
                  <button key={s.id} className="kanban-move-option"
                    onClick={() => { onMoveStage(deal, s.id); setShowMoveMenu(false); }}>
                    <span className="kanban-move-dot" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="kanban-card-delete"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            title="Delete deal">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div className="kanban-card-name">{deal.name}</div>
      {deal.industry && (
        <div className="kanban-card-industry">
          <Building2 size={12} /><span>{deal.industry}</span>
        </div>
      )}
      <div className="kanban-card-meta">
        {deal.value && (
          <span className="kanban-card-value">
            <DollarSign size={12} />{formatCurrency(deal.value)}
          </span>
        )}
        {deal.closingDate && (
          <span className="kanban-card-close">
            <Calendar size={12} />{formatDate(deal.closingDate)}
          </span>
        )}
      </div>
      {deal.owner && (
        <div className="kanban-card-owner">
          <UserCircle size={12} /><span>{deal.owner}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Deal Detail
// ─────────────────────────────────────────────
function DealDetail({ deal, onBack, documents, allDeals, orgUsers, onOpenAnnotate, onUpdateDeal, currentUser, logAudit, onOpenEdit, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'tasks');
  const [dealDocuments, setDealDocuments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => { fetchDealData(); }, [deal.id]);

  const fetchDealData = async () => {
    try {
      const [docsRes, tasksRes, contactsRes] = await Promise.all([
        dealsApi.getDocuments(deal.id),
        dealsApi.getTasks(deal.id).catch(() => ({ data: [] })),
        dealsApi.getContacts(deal.id).catch(() => ({ data: [] })),
      ]);
      setDealDocuments((docsRes.data || []).map(doc => ({ ...doc, jobId: doc.id || doc.jobId })));
      setTasks(tasksRes.data || []);
      setContacts(contactsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch deal data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      notation: 'compact', maximumFractionDigits: 1,
    }).format(value);
  };

  const stage = getStage(deal.stage);

  const canEdit = can(currentUser, 'edit');

  const isSaleLeaseback = deal.deal_type_id === 'sale_leaseback_industrial';

  const handleMilestoneUpdate = async (milestoneKey) => {
    try {
      const updated = { ...(deal.milestone_dates || {}), [milestoneKey]: new Date().toISOString() };
      await dealsApi.update(deal.id, { milestone_dates: updated });
      if (onUpdateDeal) onUpdateDeal(deal.id, { milestone_dates: updated });
    } catch (err) {
      console.error('Failed to update milestone dates:', err);
    }
  };

  const tabs = [
    { id: 'tasks',        label: 'Tasks',           icon: CheckSquare, count: tasks.length },
    { id: 'documents',    label: 'Documents',        icon: FileText,    count: dealDocuments.length },
    { id: 'contacts',     label: 'Contacts',         icon: Users,       count: contacts.length },
    { id: 'irl',          label: 'IRL',                 icon: Target,   count: null },
    { id: 'team',         label: 'Deal Team',         icon: UserCheck, count: null },
    { id: 'timeline',     label: 'Timeline',         icon: GitCommitHorizontal, count: null },
    { id: 'notes',        label: 'Notes',            icon: StickyNote,  count: null },
    { id: 'intelligence', label: 'Intelligence',     icon: TrendingUp,  count: null },
    ...(currentUser?.role === 'admin' || currentUser?.isAdmin ? [{ id: 'permissions', label: 'Deal Permissions', icon: Shield, count: null }] : []),
    { id: 'portal', label: 'Deal Layer', icon: Layers, count: null },
  ];

  return (
    <div className="deal-detail-container">
      <div className="deal-detail-header">
        <button className="deal-detail-back" onClick={onBack}>
          <ArrowLeft size={18} /><span>Back to Pipeline</span>
        </button>
      </div>

      <div className="deal-detail-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h2 className="deal-detail-name">{deal.name}</h2>
            {isSaleLeaseback && (
              <ThirtyDayCloseTracker deal={deal} onMilestoneUpdate={handleMilestoneUpdate} />
            )}
          </div>
          {isSaleLeaseback && (
            <div style={{ marginLeft: 16, flexShrink: 0 }}>
              <DealGradeWidget deal={deal} />
            </div>
          )}
        </div>
        <div className="deal-detail-title-row">
          <div className="deal-detail-title-section">
            <span className="deal-detail-stage" style={{ backgroundColor: `${stage.color}18`, color: stage.color }}>
              {stage.name}
            </span>
            <span className="deal-detail-status">Active</span>
          </div>
          {canEdit && (
            <button className="deal-edit-card-btn" onClick={() => onOpenEdit(deal)} title="Edit Deal">
              <Edit3 size={15} />
              <span>Edit</span>
            </button>
          )}
        </div>

        <div className="deal-detail-metrics">
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><DollarSign size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Value</span>
              <span className="deal-detail-metric-value">{formatCurrency(deal.value)}</span>
            </div>
          </div>
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><Building2 size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Industry</span>
              <span className="deal-detail-metric-value">{deal.industry || '—'}</span>
            </div>
          </div>
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><Calendar size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Target Close</span>
              <span className="deal-detail-metric-value">
                {deal.closingDate ? new Date(deal.closingDate).toLocaleDateString() : '—'}
              </span>
            </div>
          </div>
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><Clock size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Owner</span>
              <span className="deal-detail-metric-value">{deal.owner || 'Unassigned'}</span>
            </div>
          </div>
        </div>

        <div className="deal-detail-tabs">
          {tabs.map(tab => (
            <button key={tab.id}
              className={`deal-detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}>
              <tab.icon size={14} className="deal-detail-tab-icon" />
              <span className="deal-detail-tab-label">{tab.label}</span>
              {tab.count > 0 && <span className="deal-detail-tab-count">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="deal-detail-content">
        {activeTab === 'tasks' && (
          <TasksTab dealId={deal.id} tasks={tasks} setTasks={setTasks} onUpdate={fetchDealData} canEdit={canEdit} orgUsers={orgUsers} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab
            dealId={deal.id}
            documents={dealDocuments}
            allDocuments={documents}
            allDeals={allDeals}
            onUpdate={fetchDealData}
            onViewDocument={setViewingDoc}
            logAudit={logAudit}
            dealName={deal.name}
            canEdit={canEdit}
            dealTypeId={deal.deal_type_id}
          />
        )}
        {activeTab === 'contacts' && (
          <ContactsTab dealId={deal.id} contacts={contacts} onUpdate={fetchDealData} canEdit={canEdit} />
        )}
        {activeTab === 'diligence' && (
          <DiligenceTracker selectedDeal={deal} orgUsers={orgUsers} onViewDocument={setViewingDoc} currentUser={currentUser} />
        )}
        {activeTab === 'timeline' && (
          <DealTimeline deal={deal} onUpdate={onUpdateDeal} />
        )}
        {activeTab === 'notes' && (
          <NotesTab dealId={deal.id} currentUser={currentUser} canEdit={canEdit} />
        )}
        {activeTab === 'intelligence' && (
          <IntelligenceTab deal={deal} />
        )}
        {activeTab === 'irl' && (
          <IRLModule dealId={deal.id} canEdit={canEdit} orgUsers={orgUsers} />
        )}
        {activeTab === 'team' && (
          <DealTeamTab dealId={deal.id} orgUsers={orgUsers} canEdit={canEdit} />
        )}
        {activeTab === 'permissions' && (
          <DealPermissions deal={deal} currentUser={currentUser} />
        )}
        {activeTab === 'portal' && (
          <DealPortalTab
            dealId={deal.id}
            canEdit={canEdit}
            currentUser={currentUser}
          />
        )}
      </div>
      {viewingDoc && <DocumentViewerModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────
// Tasks Tab
// ─────────────────────────────────────────────
const TASK_PRIORITIES = ['High', 'Medium', 'Low'];
const PRIORITY_COLORS = { High: '#EF4444', Medium: '#F59E0B', Low: '#6B7280' };

function TasksTab({ dealId, tasks, setTasks, onUpdate, canEdit = true, orgUsers = [] }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', priority: 'Medium', assignee: '', dueDate: '' });
  const [saving, setSaving] = useState(false);

  const toggleStatus = (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    dealsApi.updateTask(dealId, task.id, { status: newStatus }).catch(() => {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
    });
  };

  const deleteTask = async (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    await dealsApi.deleteTask(dealId, taskId).catch(() => onUpdate());
  };

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await dealsApi.createTask(dealId, {
        title: form.title.trim(),
        priority: form.priority,
        assignee: form.assignee || null,
        due_date: form.dueDate || null,
        status: 'pending',
      });
      setForm({ title: '', priority: 'Medium', assignee: '', dueDate: '' });
      setShowAdd(false);
      onUpdate();
    } catch (err) { console.error('Failed to add task:', err); }
    finally { setSaving(false); }
  };

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Tasks ({tasks.length})</h3>
        {canEdit && (
          <button className="deal-btn-add" onClick={() => setShowAdd(s => !s)}>
            <Plus size={16} />{showAdd ? 'Cancel' : 'Add Task'}
          </button>
        )}
      </div>

      {showAdd && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            type="text"
            placeholder="Task title..."
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            autoFocus
            style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
              style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}>
              {TASK_PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
            <select value={form.assignee} onChange={e => setForm(f => ({ ...f, assignee: e.target.value }))}
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}>
              <option value="">Unassigned</option>
              {orgUsers.map(u => { const n = u.full_name || u.name || u.email; return <option key={u.id} value={n}>{n}</option>; })}
            </select>
            <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="deal-btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="deal-btn-add" onClick={handleAdd} disabled={saving || !form.title.trim()}>
              {saving ? 'Adding...' : 'Add Task'}
            </button>
          </div>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><CheckSquare size={40} /></div>
          <h4>No tasks</h4><p>Add tasks to track deal work</p>
        </div>
      ) : (
        <div className="deal-tasks-list">
          {tasks.map(task => {
            const done = task.status === 'completed';
            const dueDate = task.due_date || task.dueDate;
            const overdue = dueDate && !done && new Date(dueDate + 'T00:00:00') < new Date(new Date().toDateString());
            return (
              <div key={task.id} className={`deal-task-item ${task.status}`} style={{ alignItems: 'flex-start' }}>
                <input type="checkbox" checked={done} onChange={() => toggleStatus(task)} style={{ marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span className="deal-task-title" style={{ textDecoration: done ? 'line-through' : 'none', color: done ? '#9CA3AF' : '#111827' }}>
                    {task.title}
                  </span>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    {task.priority && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: PRIORITY_COLORS[task.priority] || '#6B7280' }}>
                        {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                      </span>
                    )}
                    {(task.assignee || task.assigned_to) && (
                      <span style={{ fontSize: 11, color: '#6B7280' }}>→ {task.assignee || task.assigned_to}</span>
                    )}
                    {dueDate && (
                      <span style={{ fontSize: 11, color: overdue ? '#EF4444' : '#6B7280', fontWeight: overdue ? 600 : 400 }}>
                        {overdue ? '⚠ ' : ''}Due {new Date(dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <button onClick={() => deleteTask(task.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D1D5DB', padding: 2, flexShrink: 0 }}>
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Documents Tab
// ─────────────────────────────────────────────
const DOC_CATEGORIES = [
  'Financial', 'Legal', 'Operational', 'HR', 'IT', 'Insurance',
  'Facilities', 'Sales', 'Procurement', 'Tax', 'General'
];

const SLB_CATEGORY_MAP = {
  alta_title: 'ALTA Title', phase1_esa: 'Phase I ESA', pca: 'PCA',
  building_specs: 'Building Specs', zoning: 'Zoning', market_rent: 'Market Rent',
  property_tax: 'Property Tax', liens: 'Lien Search', property_insurance: 'Property Insurance',
  financial_statements: 'Financial Statements', tax_returns: 'Tax Returns',
  t12_pl: 'Trailing 12 P&L', pe_sponsor: 'PE Sponsor', customer_concentration: 'Customer Concentration',
  lease_confirmation: 'Lease Confirmation', key_contracts: 'Key Contracts',
  debt_schedule: 'Debt Schedule', industry_context: 'Industry Context',
};

const DOC_CATEGORY_COLORS = {
  Financial:   { bg: '#DBEAFE', color: '#1D4ED8' },
  Legal:       { bg: '#EDE9FE', color: '#6D28D9' },
  Operational: { bg: '#FEF3C7', color: '#92400E' },
  HR:          { bg: '#FCE7F3', color: '#9D174D' },
  IT:          { bg: '#CFFAFE', color: '#0E7490' },
  Insurance:   { bg: '#D1FAE5', color: '#065F46' },
  Facilities:  { bg: '#FEE2E2', color: '#991B1B' },
  Sales:       { bg: '#FDE68A', color: '#78350F' },
  Procurement: { bg: '#E0E7FF', color: '#3730A3' },
  Tax:         { bg: '#D1FAE5', color: '#065F46' },
  General:     { bg: '#F3F4F6', color: '#374151' },
  'ALTA Title': { bg: '#DBEAFE', color: '#1D4ED8' },
  'Phase I ESA': { bg: '#FEE2E2', color: '#991B1B' },
  'PCA': { bg: '#FEF3C7', color: '#92400E' },
  'Building Specs': { bg: '#E0E7FF', color: '#3730A3' },
  'Zoning': { bg: '#EDE9FE', color: '#6D28D9' },
  'Market Rent': { bg: '#FDE68A', color: '#78350F' },
  'Financial Statements': { bg: '#DBEAFE', color: '#1D4ED8' },
  'Tax Returns': { bg: '#D1FAE5', color: '#065F46' },
  'Trailing 12 P&L': { bg: '#CFFAFE', color: '#0E7490' },
  'PE Sponsor': { bg: '#FCE7F3', color: '#9D174D' },
  'Customer Concentration': { bg: '#FEF3C7', color: '#92400E' },
  'Lease Confirmation': { bg: '#EDE9FE', color: '#6D28D9' },
  'Key Contracts': { bg: '#E0E7FF', color: '#3730A3' },
  'Debt Schedule': { bg: '#FEE2E2', color: '#991B1B' },
};

function formatCategory(raw) {
  if (!raw) return 'General';
  return SLB_CATEGORY_MAP[raw] || raw;
}

function getCategoryStyle(category) {
  const label = formatCategory(category);
  return DOC_CATEGORY_COLORS[label] || { bg: '#F3F4F6', color: '#374151' };
}

const SLB_REQUIRED_DOCS = [
  { id: 'alta_title', label: 'ALTA Title Commitment', track: 'Property' },
  { id: 'phase1_esa', label: 'Phase I ESA', track: 'Property' },
  { id: 'pca', label: 'Property Condition Assessment', track: 'Property' },
  { id: 'building_specs', label: 'Building Specifications', track: 'Property' },
  { id: 'financial_statements', label: 'Audited Financial Statements', track: 'Tenant Credit' },
  { id: 'tax_returns', label: 'Tax Returns', track: 'Tenant Credit' },
  { id: 't12_pl', label: 'Trailing 12 P&L', track: 'Tenant Credit' },
  { id: 'customer_concentration', label: 'Customer Concentration', track: 'Tenant Credit' },
  { id: 'lease_confirmation', label: 'Lease / LOI Confirmation', track: 'Tenant Credit' },
];

function DocumentsTab({ dealId, documents, allDocuments, allDeals, onUpdate, onViewDocument, logAudit, dealName, canEdit = true, dealTypeId }) {
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [openCategoryMenu, setOpenCategoryMenu] = useState(null);
  const [categoryUpdating, setCategoryUpdating] = useState(null);
  const uploadInputRef = React.useRef(null);

  // Close category menu on outside click
  useEffect(() => {
    if (!openCategoryMenu) return;
    const close = () => setOpenCategoryMenu(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openCategoryMenu]);

  const handleCategoryChange = async (docId, newCategory) => {
    setCategoryUpdating(docId);
    setOpenCategoryMenu(null);
    try {
      await documentsApi.updateCategory(docId, newCategory);
      onUpdate();
    } catch (err) {
      console.error('Failed to update category:', err);
    } finally {
      setCategoryUpdating(null);
    }
  };

  const docIdsInOtherDeals = allDeals
    .filter(d => d.id !== dealId)
    .flatMap(d => d.documents || []);

  const unassignedDocs = allDocuments.filter(doc =>
    !documents.some(d => d.jobId === doc.jobId) &&
    !docIdsInOtherDeals.includes(doc.jobId)
  );

  const toggleDocSelection = (docId) =>
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );

  const handleAddDocuments = async () => {
    if (selectedDocIds.length === 0) return;
    setIsAdding(true);
    try {
      for (const docId of selectedDocIds)
        await dealsApi.addDocument(dealId, docId);
      setSelectedDocIds([]);
      setShowAddDoc(false);
      onUpdate();
    } catch (err) { console.error('Failed to add documents:', err); }
    finally { setIsAdding(false); }
  };

  const selectAll = () =>
    setSelectedDocIds(
      selectedDocIds.length === unassignedDocs.length ? [] : unassignedDocs.map(d => d.jobId)
    );

  const handleRemoveDocument = async (docId) => {
    try {
      await dealsApi.removeDocument(dealId, docId);
      onUpdate();
    } catch (err) { console.error('Failed to remove document:', err); }
  };

  const handleUploadFile = async (files) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await documentsApi.upload(file, (p) => {
          // Show overall progress across all files
          const overall = Math.round(((i + p / 100) / files.length) * 100);
          setUploadProgress(overall);
        });
        const jobId = res.data?.jobId;
        if (jobId) {
          await dealsApi.addDocument(dealId, jobId);
          if (logAudit) logAudit('document_uploaded', 'documents', `Uploaded "${file.name}" to deal: ${dealName || dealId}`);
        }
      }
      onUpdate();
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Documents ({documents.length})</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {canEdit && <>
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.csv,.txt,.jpg,.jpeg,.png,.gif,.webp"
              onChange={e => handleUploadFile(e.target.files)}
            />
            <button className="deal-btn-add" onClick={() => uploadInputRef.current?.click()} disabled={isUploading}>
              <Upload size={16} />{isUploading ? `Uploading ${uploadProgress}%` : 'Upload New'}
            </button>
            <button className="deal-btn-add" onClick={() => setShowAddDoc(true)}>
              <Plus size={16} />Add Existing
            </button>
          </>}
        </div>
      </div>
      {uploadError && <div style={{ color: '#EF4444', fontSize: 13, marginBottom: 8 }}>{uploadError}</div>}

      {dealTypeId === 'sale_leaseback_industrial' && (() => {
        const presentCats = new Set(documents.map(d => d.category || d.documentType || ''));
        const received = SLB_REQUIRED_DOCS.filter(r => presentCats.has(r.id));
        const pending = SLB_REQUIRED_DOCS.filter(r => !presentCats.has(r.id));
        return (
          <div style={{ marginBottom: 16, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Offer-Required Documents</span>
              <span style={{ fontSize: 12, color: received.length >= 7 ? '#059669' : '#D97706', fontWeight: 600 }}>
                {received.length} / {SLB_REQUIRED_DOCS.length} received
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              {SLB_REQUIRED_DOCS.map(req => {
                const have = presentCats.has(req.id);
                return (
                  <div key={req.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 16px', borderBottom: '1px solid #F3F4F6',
                    background: have ? '#F0FDF4' : '#fff',
                  }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                      background: have ? '#059669' : '#E5E7EB',
                      color: have ? '#fff' : '#9CA3AF',
                    }}>
                      {have ? '✓' : '—'}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: have ? '#065F46' : '#374151' }}>{req.label}</div>
                      <div style={{ fontSize: 10, color: '#9CA3AF' }}>{req.track} Track</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {showAddDoc && (
        <div className="deal-add-documents-panel">
          <div className="deal-add-documents-header">
            <label className="deal-select-all">
              <input type="checkbox"
                checked={selectedDocIds.length === unassignedDocs.length && unassignedDocs.length > 0}
                onChange={selectAll} />
              <span>Select All ({unassignedDocs.length})</span>
            </label>
            <span className="deal-selected-count">{selectedDocIds.length} selected</span>
          </div>
          <div className="deal-documents-select-list">
            {unassignedDocs.length === 0
              ? <p className="deal-no-docs-message">No unassigned documents available</p>
              : unassignedDocs.map(doc => (
                <label key={doc.jobId} className="deal-doc-checkbox">
                  <input type="checkbox" checked={selectedDocIds.includes(doc.jobId)}
                    onChange={() => toggleDocSelection(doc.jobId)} />
                  <FileText size={20} className="deal-doc-checkbox-icon" />
                  <div className="deal-doc-checkbox-info">
                    <span className="deal-doc-checkbox-name">{doc.filename}</span>
                    <span className="deal-doc-checkbox-type">{formatDocType(doc.documentType)}</span>
                  </div>
                </label>
              ))
            }
          </div>
          <div className="deal-add-documents-actions">
            <button className="deal-btn-cancel"
              onClick={() => { setShowAddDoc(false); setSelectedDocIds([]); }}>Cancel</button>
            <button onClick={handleAddDocuments}
              disabled={selectedDocIds.length === 0 || isAdding} className="deal-btn-add">
              {isAdding ? 'Adding...' : `Add ${selectedDocIds.length} Document${selectedDocIds.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
      {documents.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><FileText size={40} /></div>
          <h4>No documents</h4><p>Add documents to this deal</p>
        </div>
      ) : (
        <div className="deal-documents-list">
          {documents.map(doc => {
            const rawCategory = doc.category || doc.documentType || 'General';
            const category = formatCategory(rawCategory);
            const catStyle = getCategoryStyle(rawCategory);
            const isMenuOpen = openCategoryMenu === doc.jobId;
            return (
              <div key={doc.jobId} className="deal-document-item">
                <div className="deal-document-info" onClick={() => onViewDocument?.(doc)}>
                  <File size={20} className="deal-document-icon" />
                  <div className="deal-document-details">
                    <span className="deal-document-name">{doc.filename}</span>
                    <span className="deal-document-meta">
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Category badge with dropdown */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={canEdit ? (e) => { e.stopPropagation(); setOpenCategoryMenu(isMenuOpen ? null : doc.jobId); } : undefined}
                      disabled={categoryUpdating === doc.jobId}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 8px', borderRadius: 12, border: 'none',
                        fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
                        background: catStyle.bg, color: catStyle.color,
                        cursor: canEdit ? 'pointer' : 'default',
                        opacity: categoryUpdating === doc.jobId ? 0.5 : 1,
                      }}
                      title={canEdit ? 'Change category' : category}
                    >
                      {category}
                      {canEdit && <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>}
                    </button>
                    {isMenuOpen && (
                      <div
                        style={{
                          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
                          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 4, minWidth: 180,
                          maxHeight: 320, overflowY: 'auto',
                        }}
                        onMouseDown={e => e.stopPropagation()}
                      >
                        {DOC_CATEGORIES.map(cat => {
                          const cs = getCategoryStyle(cat);
                          return (
                            <button
                              key={cat}
                              onClick={(e) => { e.stopPropagation(); handleCategoryChange(doc.jobId, cat); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                width: '100%', textAlign: 'left', padding: '6px 10px',
                                border: 'none', background: cat === category ? '#F3F4F6' : 'transparent',
                                cursor: 'pointer', borderRadius: 4, fontSize: 13,
                                fontWeight: cat === category ? 600 : 400,
                              }}
                            >
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: cs.color, flexShrink: 0 }} />
                              {cat}
                            </button>
                          );
                        })}
                        <div style={{ borderTop: '1px solid #E5E7EB', margin: '4px 0', padding: '4px 10px 0', fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>SLB Categories</div>
                        {Object.entries(SLB_CATEGORY_MAP).map(([id, label]) => {
                          const cs = getCategoryStyle(id);
                          return (
                            <button
                              key={id}
                              onClick={(e) => { e.stopPropagation(); handleCategoryChange(doc.jobId, id); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                width: '100%', textAlign: 'left', padding: '6px 10px',
                                border: 'none', background: category === label ? '#F3F4F6' : 'transparent',
                                cursor: 'pointer', borderRadius: 4, fontSize: 13,
                                fontWeight: category === label ? 600 : 400,
                              }}
                            >
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: cs.color, flexShrink: 0 }} />
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <button className="deal-document-remove"
                      onClick={() => handleRemoveDocument(doc.jobId)} title="Remove from deal">
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Contacts Tab
// ─────────────────────────────────────────────
const SIDE_OPTIONS = [
  { value: '', label: 'Select side…' },
  { value: 'buy_side', label: 'Buy Side' },
  { value: 'sell_side', label: 'Sell Side' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'lender', label: 'Lender' },
  { value: 'other', label: 'Other' },
];

const EMPTY_CONTACT = { name: '', role: '', email: '', phone: '', side: '' };

function ContactsTab({ dealId, contacts, onUpdate, canEdit = true }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [crmContacts, setCrmContacts] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const field = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const openAdd = () => { setForm(EMPTY_CONTACT); setEditingId(null); setShowForm(true); setShowPicker(false); };
  const openEdit = (c) => { setForm({ name: c.name, role: c.role || '', email: c.email || '', phone: c.phone || '', side: c.side || '' }); setEditingId(c.id); setShowForm(true); setShowPicker(false); };
  const cancel = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_CONTACT); };

  const openPicker = async () => {
    setShowPicker(true);
    setShowForm(false);
    setPickerSearch('');
    if (crmContacts.length === 0) {
      setPickerLoading(true);
      try {
        const res = await contactsApi.list();
        setCrmContacts(res.data || []);
      } catch (e) { console.error(e); }
      finally { setPickerLoading(false); }
    }
  };

  const connectContact = async (c) => {
    setSaving(true);
    try {
      await dealsApi.createContact(dealId, {
        name: c.name,
        role: c.title || '',
        email: c.email || '',
        phone: c.phone || '',
        side: '',
      });
      setShowPicker(false);
      onUpdate();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const filteredCrmContacts = crmContacts.filter(c => {
    const q = pickerSearch.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q);
  });

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await dealsApi.updateContact(dealId, editingId, form);
      } else {
        await dealsApi.createContact(dealId, form);
      }
      cancel();
      onUpdate();
    } catch (err) { console.error('Failed to save contact:', err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (contactId) => {
    try {
      await dealsApi.deleteContact(dealId, contactId);
      onUpdate();
    } catch (err) { console.error('Failed to delete contact:', err); }
  };

  const sideLabel = (val) => SIDE_OPTIONS.find(o => o.value === val)?.label || '';

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Contacts ({contacts.length})</h3>
        {canEdit && (
          <div className="deal-tab-header-actions">
            <button className="deal-btn-secondary" onClick={openPicker}><Link size={14} />Connect</button>
            <button className="deal-btn-add" onClick={openAdd}><Plus size={16} />Add New</button>
          </div>
        )}
      </div>

      {showPicker && (
        <div className="contact-picker-card">
          <div className="contact-picker-header">
            <span>Connect from Contacts</span>
            <button className="deal-contact-btn" onClick={() => setShowPicker(false)}><X size={14} /></button>
          </div>
          <input
            className="contact-picker-search"
            type="text"
            placeholder="Search by name, email, or company…"
            value={pickerSearch}
            onChange={e => setPickerSearch(e.target.value)}
            autoFocus
          />
          <div className="contact-picker-list">
            {pickerLoading && <div className="contact-picker-empty">Loading…</div>}
            {!pickerLoading && filteredCrmContacts.length === 0 && (
              <div className="contact-picker-empty">No contacts found</div>
            )}
            {!pickerLoading && filteredCrmContacts.map(c => (
              <div key={c.id} className="contact-picker-item" onClick={() => connectContact(c)}>
                <div className="contact-picker-avatar">{c.name?.charAt(0).toUpperCase()}</div>
                <div className="contact-picker-info">
                  <span className="contact-picker-name">{c.name}</span>
                  {c.title && <span className="contact-picker-meta">{c.title}</span>}
                  {c.company && <span className="contact-picker-meta">{c.company}</span>}
                  {c.email && <span className="contact-picker-meta">{c.email}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="contact-form-card">
          <div className="contact-form-title">{editingId ? 'Edit Contact' : 'New Contact'}</div>
          <div className="contact-form-grid">
            <div className="contact-form-field">
              <label>Name *</label>
              <input type="text" placeholder="Full name" value={form.name} onChange={field('name')} />
            </div>
            <div className="contact-form-field">
              <label>Role / Title</label>
              <input type="text" placeholder="e.g. CFO" value={form.role} onChange={field('role')} />
            </div>
            <div className="contact-form-field">
              <label>Email</label>
              <input type="email" placeholder="email@example.com" value={form.email} onChange={field('email')} />
            </div>
            <div className="contact-form-field">
              <label>Phone</label>
              <input type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={field('phone')} />
            </div>
            <div className="contact-form-field">
              <label>Side</label>
              <select value={form.side} onChange={field('side')}>
                {SIDE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="contact-form-actions">
            <button className="deal-btn-cancel" onClick={cancel}>Cancel</button>
            <button className="deals-btn-primary" onClick={handleSave} disabled={!form.name.trim() || saving}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </div>
      )}

      {contacts.length === 0 && !showForm ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><Users size={40} /></div>
          <h4>No contacts</h4><p>Add contacts for this deal</p>
        </div>
      ) : (
        <div className="deal-contacts-list">
          {contacts.map(contact => (
            <div key={contact.id} className="deal-contact-item">
              <div className="deal-contact-avatar">{contact.name?.charAt(0).toUpperCase()}</div>
              <div className="deal-contact-info">
                <div className="deal-contact-top">
                  <span className="deal-contact-name">{contact.name}</span>
                  {contact.side && <span className="deal-contact-side">{sideLabel(contact.side)}</span>}
                </div>
                {contact.role && <span className="deal-contact-role">{contact.role}</span>}
                <div className="deal-contact-details">
                  {contact.email && <span className="deal-contact-email">{contact.email}</span>}
                  {contact.phone && <span className="deal-contact-phone">{contact.phone}</span>}
                </div>
              </div>
              {canEdit && (
                <div className="deal-contact-actions">
                  <button className="deal-contact-btn" onClick={() => openEdit(contact)} title="Edit"><Edit3 size={13} /></button>
                  <button className="deal-contact-btn deal-contact-btn--danger" onClick={() => handleDelete(contact.id)} title="Delete"><Trash2 size={13} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Notes Tab
// ─────────────────────────────────────────────
function NotesTab({ dealId, currentUser, canEdit = true }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchNotes(); }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotes = async () => {
    try {
      const res = await dealsApi.getNotes(dealId);
      setNotes(res.data || []);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const authorName = currentUser?.name || currentUser?.email || 'Unknown';
      const res = await dealsApi.addNote(dealId, newNote.trim());
      setNotes(prev => [res.data, ...prev]);
      setNewNote('');
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await dealsApi.deleteNote(dealId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const formatTs = (ts) =>
    new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Notes ({notes.length})</h3>
      </div>

      {/* Compose box */}
      {canEdit && <div className="notes-compose">
        <textarea
          className="notes-textarea"
          placeholder="Write a note..."
          value={newNote}
          onChange={e => setNewNote(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote(); }}
          rows={3}
        />
        <div className="notes-compose-footer">
          <span className="notes-hint">Cmd+Enter to submit</span>
          <button
            className="deals-btn-primary notes-submit-btn"
            onClick={handleAddNote}
            disabled={saving || !newNote.trim()}
          >
            <Send size={14} /> {saving ? 'Saving…' : 'Add Note'}
          </button>
        </div>
      </div>}

      {/* Notes list */}
      {notes.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><StickyNote size={40} /></div>
          <h4>No notes yet</h4>
          <p>Add a note to keep track of important details</p>
        </div>
      ) : (
        <div className="notes-list">
          {notes.map(note => (
            <div key={note.id} className="note-item">
              <div className="note-header">
                <div className="note-author-row">
                  <span className="note-avatar">{note.authorName?.[0]?.toUpperCase() || '?'}</span>
                  <span className="note-author">{note.authorName}</span>
                  <span className="note-time">{formatTs(note.createdAt)}</span>
                </div>
                {canEdit && (
                  <button
                    className="note-delete-btn"
                    onClick={() => handleDeleteNote(note.id)}
                    title="Delete note"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <p className="note-text">{note.content || note.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Outcome Exit Modal
// Shown when a deal is moved to archived or discontinued
// ─────────────────────────────────────────────
function OutcomeExitModal({ deal, targetStage, onSubmit, onCancel }) {
  const [outcome, setOutcome] = useState(targetStage === 'discontinued' ? 'passed' : 'closed');
  const [outcomeReason, setOutcomeReason] = useState('');
  const [outcomeDetails, setOutcomeDetails] = useState('');
  const [finalValue, setFinalValue] = useState(deal.value || '');
  const [dealQualityScore, setDealQualityScore] = useState('');
  const [teamNotes, setTeamNotes] = useState('');

  const reasons = OUTCOME_REASONS[outcome] || [];

  return (
    <div className="deals-modal-overlay" onClick={onCancel}>
      <div className="deals-modal deals-outcome-modal" onClick={e => e.stopPropagation()}>
        <div className="deals-modal-header">
          <div className="deals-modal-title-row">
            <Target size={20} />
            <h2>Capture Deal Outcome</h2>
          </div>
          <button className="deals-modal-close" onClick={onCancel}><X size={20} /></button>
        </div>

        <div className="deals-modal-body">
          <p className="deals-outcome-intro">
            Before archiving <strong>"{deal.name}"</strong>, capture the outcome. This data improves your team's deal intelligence over time.
          </p>

          <div className="deals-form-group">
            <label>Outcome *</label>
            <div className="outcome-options">
              {[
                { value: 'closed',          label: '✓  Deal Closed',          color: '#10b981' },
                { value: 'passed',          label: '↩  Passed / Declined',    color: '#f59e0b' },
                { value: 'lost_competitor', label: '✗  Lost to Competitor',   color: '#ef4444' },
                { value: 'deal_died',       label: '—  Deal Died',            color: '#6b7280' },
              ].map(o => (
                <button key={o.value}
                  type="button"
                  className={`outcome-option-btn ${outcome === o.value ? 'selected' : ''}`}
                  style={outcome === o.value ? { borderColor: o.color, color: o.color, background: `${o.color}10` } : {}}
                  onClick={() => { setOutcome(o.value); setOutcomeReason(''); }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {reasons.length > 0 && (
            <div className="deals-form-group">
              <label>Primary Reason</label>
              <select value={outcomeReason} onChange={e => setOutcomeReason(e.target.value)}>
                <option value="">Select reason…</option>
                {reasons.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}

          <div className="deals-form-group">
            <label>Additional Notes</label>
            <textarea
              rows={3}
              placeholder="What happened? Key factors, lessons learned…"
              value={outcomeDetails}
              onChange={e => setOutcomeDetails(e.target.value)}
              className="deals-outcome-textarea"
            />
          </div>

          <div className="deals-form-row">
            <div className="deals-form-group">
              <label>Final Value ($)</label>
              <div className="deals-input-with-icon">
                <DollarSign size={16} className="deals-input-icon" />
                <input type="number" placeholder="Actual transaction value"
                  value={finalValue} onChange={e => setFinalValue(e.target.value)} />
              </div>
            </div>
            <div className="deals-form-group">
              <label>Deal Quality Score (1–5)</label>
              <select value={dealQualityScore} onChange={e => setDealQualityScore(e.target.value)}>
                <option value="">Rate overall quality…</option>
                {[1,2,3,4,5].map(n => (
                  <option key={n} value={n}>{n} — {['Very Poor','Poor','Average','Good','Excellent'][n-1]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="deals-form-group">
            <label>Internal Team Notes</label>
            <textarea
              rows={2}
              placeholder="Private notes for your team (not shared externally)…"
              value={teamNotes}
              onChange={e => setTeamNotes(e.target.value)}
              className="deals-outcome-textarea"
            />
          </div>
        </div>

        <div className="deals-modal-footer">
          <button className="deals-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="deals-btn-primary"
            onClick={() => onSubmit(deal, targetStage, { outcome, outcomeReason, outcomeDetails, finalValue, dealQualityScore, teamNotes })}
          >
            <Award size={16} /> Save & Archive Deal
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Intelligence Tab — shows captured deal data points
// ─────────────────────────────────────────────
function IntelligenceTab({ deal }) {
  const fmt = (v) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v) : '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const multiple = deal.finalValue && deal.value ? (deal.finalValue / deal.value).toFixed(2) : null;

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Deal Intelligence</h3>
      </div>

      <div className="intel-grid">
        {/* Classification */}
        <div className="intel-card">
          <div className="intel-card-title"><TrendingUp size={14} /> Classification</div>
          <div className="intel-row"><span>Type</span><strong>{deal.dealType?.replace(/_/g,' ') || '—'}</strong></div>
          <div className="intel-row"><span>Source</span><strong>{deal.dealSource?.replace(/_/g,' ') || '—'}</strong></div>
          <div className="intel-row"><span>Geography</span><strong>{deal.geography || '—'}</strong></div>
        </div>

        {/* Entry Financials */}
        <div className="intel-card">
          <div className="intel-card-title"><DollarSign size={14} /> Entry Financials</div>
          <div className="intel-row"><span>Entry Value</span><strong>{fmt(deal.value)}</strong></div>
          <div className="intel-row"><span>Revenue</span><strong>{fmt(deal.entryRevenue)}</strong></div>
          <div className="intel-row"><span>EBITDA</span><strong>{fmt(deal.entryEbitda)}</strong></div>
          {deal.entryRevenue && deal.value && (
            <div className="intel-row"><span>EV/Revenue</span><strong>{(deal.value / deal.entryRevenue).toFixed(1)}x</strong></div>
          )}
          {deal.entryEbitda && deal.value && (
            <div className="intel-row"><span>EV/EBITDA</span><strong>{(deal.value / deal.entryEbitda).toFixed(1)}x</strong></div>
          )}
        </div>

        {/* Timeline */}
        <div className="intel-card">
          <div className="intel-card-title"><Calendar size={14} /> Timeline</div>
          <div className="intel-row"><span>Created</span><strong>{fmtDate(deal.createdAt)}</strong></div>
          <div className="intel-row"><span>IOI</span><strong>{fmtDate(deal.ioiDate)}</strong></div>
          <div className="intel-row"><span>LOI</span><strong>{fmtDate(deal.loiDate)}</strong></div>
          <div className="intel-row"><span>Diligence & Agreement</span><strong>{fmtDate(deal.diligenceAgreementDate)}</strong></div>
          <div className="intel-row"><span>Closing</span><strong>{fmtDate(deal.closingDate)}</strong></div>
          <div className="intel-row"><span>Post Close Completed</span><strong>{fmtDate(deal.postCloseDate)}</strong></div>
          {deal.diligenceAgreementDate && deal.loiDate && (
            <div className="intel-row"><span>LOI → Close Duration</span><strong>{Math.round((new Date(deal.diligenceAgreementDate) - new Date(deal.loiDate)) / 86400000)} days</strong></div>
          )}
        </div>

        {/* Outcome */}
        {deal.outcome && (
          <div className="intel-card">
            <div className="intel-card-title"><Award size={14} /> Outcome</div>
            <div className="intel-row"><span>Result</span><strong style={{ textTransform: 'capitalize' }}>{deal.outcome?.replace(/_/g,' ') || '—'}</strong></div>
            {deal.outcomeReason && <div className="intel-row"><span>Reason</span><strong>{deal.outcomeReason}</strong></div>}
            {deal.finalValue && <div className="intel-row"><span>Final Value</span><strong>{fmt(deal.finalValue)}</strong></div>}
            {multiple && <div className="intel-row"><span>Value vs Target</span><strong>{multiple}x</strong></div>}
            {deal.dealQualityScore && <div className="intel-row"><span>Quality Score</span><strong>{deal.dealQualityScore}/5</strong></div>}
            {deal.negotiationRounds && <div className="intel-row"><span>Negotiation Rounds</span><strong>{deal.negotiationRounds}</strong></div>}
            {deal.outcomeDetails && (
              <div className="intel-notes">
                <span className="intel-notes-label">Notes</span>
                <p>{deal.outcomeDetails}</p>
              </div>
            )}
          </div>
        )}

        {!deal.outcome && (
          <div className="intel-card intel-card-empty">
            <div className="intel-card-title"><Award size={14} /> Outcome</div>
            <p className="intel-empty-text">Outcome will be captured when this deal is archived or discontinued.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Deal Team Tab ─────────────────────────────────────────────────────────────
const TEAM_ROLES = ['lead', 'sponsor', 'analyst', 'associate', 'advisor', 'observer'];

function DealTeamTab({ dealId, orgUsers = [], canEdit }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState('analyst');

  useEffect(() => { if (dealId) fetchTeam(); }, [dealId]); // eslint-disable-line

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const res = await dealsApi.getTeam(dealId);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to fetch team', e);
    } finally {
      setLoading(false);
    }
  };

  const addMember = async () => {
    if (!addUserId) return;
    setSaving(true);
    try {
      await dealsApi.addTeamMember(dealId, addUserId, addRole);
      setAddUserId('');
      setAddRole('analyst');
      fetchTeam();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const removeMember = async (memberId) => {
    try {
      await dealsApi.removeTeamMember(dealId, memberId);
      fetchTeam();
    } catch (e) { console.error(e); }
  };

  const memberUserIds = new Set(members.map(m => m.user_id));
  const availableUsers = orgUsers.filter(u => !memberUserIds.has(u.id));

  if (loading) return <div className="deal-tab-panel" style={{ color: '#9CA3AF' }}>Loading team...</div>;

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Deal Team ({members.length})</h3>
      </div>

      {/* Add member row */}
      {canEdit && (
        availableUsers.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <select
              value={addUserId}
              onChange={e => setAddUserId(e.target.value)}
              style={{ flex: 1, padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
            >
              <option value="">Select team member to add...</option>
              {availableUsers.map(u => (
                <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
              ))}
            </select>
            <select
              value={addRole}
              onChange={e => setAddRole(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
            >
              {TEAM_ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
            <button
              className="deal-btn-add"
              onClick={addMember}
              disabled={!addUserId || saving}
            ><Plus size={15} /> {saving ? 'Adding...' : 'Add'}</button>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 16 }}>All organization members have been added to this deal.</p>
        )
      )}

      {members.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><UserCheck size={40} /></div>
          <h4>No team members</h4>
          <p>{canEdit ? 'Add team members to track who is working on this deal.' : 'No team members assigned.'}</p>
        </div>
      ) : (
        <div className="deal-contacts-list">
          {members.map(m => {
            const user = m.users || {};
            const name = user.full_name || user.email || 'Unknown';
            return (
              <div key={m.id} className="deal-contact-item">
                <div className="deal-contact-avatar">{name.charAt(0).toUpperCase()}</div>
                <div className="deal-contact-info">
                  <div className="deal-contact-top">
                    <span className="deal-contact-name">{name}</span>
                    <span className="deal-contact-side" style={{ background: '#EFF6FF', color: '#3B82F6' }}>
                      {(m.role || 'analyst').charAt(0).toUpperCase() + (m.role || 'analyst').slice(1)}
                    </span>
                  </div>
                  {user.email && <span className="deal-contact-role">{user.email}</span>}
                </div>
                {canEdit && (
                  <div className="deal-contact-actions">
                    <button
                      className="deal-contact-btn deal-contact-btn--danger"
                      onClick={() => removeMember(m.id)}
                      title="Remove"
                    ><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Deals;
